import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS } from "@/data/content";
import { WhatsAppButton } from "./WhatsAppButton";
import { cn } from "@/lib/utils";

const LangToggle = ({ compact }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className="inline-flex items-center rounded-full border border-[rgba(201,169,110,0.32)] p-0.5"
      data-testid="language-toggle"
      role="group"
      aria-label="Language"
    >
      {["no", "en"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          data-testid={`language-toggle-${l}`}
          className={cn(
            "px-3 py-1 text-xs uppercase tracking-[0.18em] rounded-full transition-colors",
            lang === l
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          aria-pressed={lang === l}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const NAV = [
  { id: "services", key: "services" },
  { id: "pricing", key: "pricing" },
  { id: "gallery", key: "gallery" },
  { id: "reviews", key: "reviews" },
  { id: "location", key: "location" },
  { id: "hours", key: "hours" },
];

export function Header() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b transition-colors",
        scrolled
          ? "bg-[rgba(7,7,8,0.88)] border-[rgba(201,169,110,0.22)] backdrop-blur-md"
          : "bg-[rgba(7,7,8,0.55)] border-transparent backdrop-blur-sm"
      )}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <a
            href="#top"
            data-testid="brand-logo"
            className="font-display text-xl sm:text-2xl tracking-[-0.02em] text-foreground"
          >
            Beauty By Magdalena
          </a>

          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-testid={`nav-${item.id}`}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t.nav[item.key]}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LangToggle />
            <WhatsAppButton source="header" size="default" testId="header-whatsapp-cta">
              {t.cta.quote}
            </WhatsAppButton>
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-2">
            <LangToggle compact />
            <Sheet>
              <SheetTrigger asChild>
                <button
                  className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-[rgba(201,169,110,0.32)] text-foreground"
                  aria-label="Menu"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0B0B0F] border-l border-[rgba(201,169,110,0.22)] w-[80%]">
                <div className="mt-10 flex flex-col gap-5">
                  {NAV.map((item) => (
                    <SheetClose asChild key={item.id}>
                      <a
                        href={`#${item.id}`}
                        data-testid={`mobile-nav-${item.id}`}
                        className="text-lg text-foreground/90"
                      >
                        {t.nav[item.key]}
                      </a>
                    </SheetClose>
                  ))}
                  <div className="pt-4">
                    <WhatsAppButton source="header" className="w-full" testId="mobile-whatsapp-cta">
                      {t.cta.quote}
                    </WhatsAppButton>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{BUSINESS.address}</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
